import { every, times } from '@sama/micro-dash';
import { Vector3 } from 'three';
import {
  Point3D,
  PointType,
} from '../../team-apis/otterdam/answers/shape-typings';
import { pointToVector, vectorToPoint } from './point-cloud-utils';

export function addPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 + v2);
}

export function subtractPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 - v2);
}

export function multiplyPoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 * v2);
}

export function dividePoints<P extends PointType>(p1: P, p2: P): P {
  return reducePoints([p1, p2], (v1, v2) => v1 / v2);
}

export function distanceBetween<P extends PointType>(p1: P, p2: P) {
  return Math.hypot(...subtractPoints(p1, p2));
}

export function directionBetween<P extends PointType>(p1: P, p2: P) {
  return normalizePoint(subtractPoints(p2, p1));
}

export function midPoint<P extends PointType>(points: P[]): P {
  const max = reducePoints(points, Math.max);
  const min = reducePoints(points, Math.min);
  return scalePoint(addPoints(max, min), 0.5);
}

export function scalePoint<P extends PointType>(p: P, scale: number): P {
  return mapPoint(p, (v) => v * scale);
}

export function normalizePoint<P extends PointType>(point: P) {
  const length = Math.hypot(...point);
  return scalePoint(point, 1 / length);
}

export function reducePoints<P extends PointType>(
  points: P[],
  reduceCoordinates: (v1: number, v2: number) => number,
) {
  const reduceTwoPoints = (p1: P, p2: P) =>
    times(p1.length, (i) => reduceCoordinates(p1[i], p2[i])) as P;
  return points.reduce(reduceTwoPoints);
}

export function mapPoint<P extends PointType>(
  p: P,
  fn: (value: number) => number,
) {
  return p.map(fn) as P;
}

export function dotProduct<P extends PointType>(p1: P, p2: P) {
  return times(p1.length, (i) => p1[i] * p2[i]).reduce((a, b) => a + b);
}

export function roundVectors(points: Vector3[]) {
  return points.map(roundVector);
}

export function roundVector(vector: Vector3): Point3D {
  return [roundValue(vector.x), roundValue(vector.y), roundValue(vector.z)];
}

const meaningfulDecimalForDelivery = 1e5;
export function roundValue(value: number) {
  return (
    Math.round(value * meaningfulDecimalForDelivery) /
      meaningfulDecimalForDelivery || 0 // Transform -0 into 0
  );
}

export function mapVector(v: Vector3, fn: (value: number) => number) {
  return pointToVector(mapPoint(vectorToPoint(v), fn));
}

const toleranceForDelivery =
  meaningfulDecimalForDelivery /
  (meaningfulDecimalForDelivery * meaningfulDecimalForDelivery);

export function isPointCloseTo<P extends PointType>(
  p1: P,
  p2: P,
  tolerance = toleranceForDelivery,
) {
  return every(
    p1,
    (_value, index) => Math.abs(p1[index] - p2[index]) < tolerance,
  );
}
